import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMapMarkerAlt, faCalendarAlt, faPlane, faCheckCircle,
    faTimesCircle, faGlobe, faHotel, faMoon, faBed,
    faUtensils, faClock, faMoneyBillWave, faInfoCircle,
    faBus, faPassport, faCamera, faUserTie, faTag
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../Common/Configurations/AuthContext';

const DEFAULT_IMAGE = "https://trawtelcdn.s3.eu-north-1.amazonaws.com/smarttravelcrm/package_images/default-package.jpg";

const DMCPackagePreview = ({ data, onPublish, btnLoading, isViewOnly = false }) => {
    const { user } = useAuth();
    const [heroSrc, setHeroSrc] = useState(DEFAULT_IMAGE);

    // --- 1. IMAGE LOGIC ---
    useEffect(() => {
        if (data?.media?.images && data.media.images.length > 0) {
            setHeroSrc(data.media.images[0].url);
        } else if (data?.heroImage && data.heroImage.trim() !== "") {
            setHeroSrc(data.heroImage);
        } else {
            setHeroSrc(DEFAULT_IMAGE);
        }
    }, [data?.heroImage, data?.media]);

    // --- HELPER: PRICE FORMATTER ---
    const formatPrice = (val) => {
        if (!val || val === 0) return '-';
        return `${data.pricing?.currency || 'USD'} ${Number(val).toLocaleString()}`;
    };

    // --- HELPER: PRICING LOGIC FIX ---
    const isFlightIncluded = data.flights?.included;
    let activePricing = {};

    if (data.pricing?.withFlight?.double > 0) {
        activePricing = data.pricing.withFlight;
    } else {
        activePricing = data.pricing?.landOnly || {};
    }

    // --- HELPER: DATE FORMATTER ---
    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return "";
        return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };
    const getIncludeIcon = (tag) => {
        const lowerTag = tag.toLowerCase();
        if (lowerTag.includes('hotel')) return faHotel;
        if (lowerTag.includes('flight')) return faPlane;
        if (lowerTag.includes('meal') || lowerTag.includes('food')) return faUtensils;
        if (lowerTag.includes('transfer') || lowerTag.includes('bus') || lowerTag.includes('cab')) return faBus;
        if (lowerTag.includes('visa')) return faPassport;
        if (lowerTag.includes('sight') || lowerTag.includes('tour')) return faCamera;
        if (lowerTag.includes('guide')) return faUserTie;
        return faTag;
    };

    return (
        <div className="dmc-pkg-preview-card custom-scrollbar" style={{ height: '100%', overflowY: 'auto', background: '#f8f9fa', paddingBottom: '20px' }}>

            {/* --- HERO SECTION --- */}
            <div style={{ position: 'relative', height: '300px', backgroundColor: '#333' }}>
                <img
                    src={heroSrc}
                    alt="Hero"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: '0.85' }}
                    onError={(e) => { e.currentTarget.src = DEFAULT_IMAGE; }}
                />
                <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
                    padding: '20px', color: '#fff'
                }}>
                    <span className="badge bg-info mb-2">{user?.companyname || "DMC"}</span>
                    <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#fff' }}>
                        {data.title || "Package Title"}
                    </h2>
                    <div className="d-flex align-items-center gap-3 mt-2 text-white" style={{ fontSize: '13px' }}>
                        <span><FontAwesomeIcon icon={faMoon} /> {data.totalNights} Nights / {data.totalDays} Days</span>
                        {data.validity?.from && (
                            <span><FontAwesomeIcon icon={faCalendarAlt} /> Valid: {formatDate(data.validity.from)} - {formatDate(data.validity.to)}</span>
                        )}
                    </div>
                </div>
            </div>

            <div className="p-4">

                {/* --- 0. INCLUDES BADGES (Added Here) --- */}
                {data.includes?.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mb-4">
                        {data.includes.map((tag, i) => (
                            <div key={i} className="badge bg-white text-dark border shadow-sm px-3 py-2 d-flex align-items-center gap-2">
                                <FontAwesomeIcon icon={getIncludeIcon(tag)} className="text-primary" />
                                <span>{tag}</span>
                            </div>
                        ))}
                    </div>
                )}
                <div className="mb-4">
                    <h5 className="fw-bold text-dark border-bottom pb-2 mb-3">
                        <FontAwesomeIcon icon={faMoneyBillWave} className="me-2 text-primary" /> 
                        Package Cost ({isFlightIncluded ? "Flights Included" : "Land Only"})
                    </h5>
                    <div className="table-responsive bg-white rounded border shadow-sm">
                        <table className="table table-bordered mb-0 text-center small">
                            <thead className="bg-light">
                                <tr>
                                    <th>Double</th>
                                    <th>Triple</th>
                                    <th>Single</th>
                                    <th>Child With Bed</th>
                                    <th>Child No Bed</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="fw-bold text-success">{formatPrice(activePricing.double)}</td>
                                    <td>{formatPrice(activePricing.triple)}</td>
                                    <td>{formatPrice(activePricing.single)}</td>
                                    <td>{formatPrice(activePricing.childWithBed)}</td>
                                    <td>{formatPrice(activePricing.childWithoutBed)}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 2. DESTINATIONS */}
                {data.destinations?.length > 0 && (
                    <div className="mb-4">
                        <h5 className="fw-bold text-dark border-bottom pb-2 mb-3">
                            <FontAwesomeIcon icon={faGlobe} className="me-2 text-primary" /> Destinations
                        </h5>
                        <div className="d-flex flex-wrap gap-2">
                            {data.destinations.map((country, idx) => (
                                <div key={idx} className="bg-white border rounded p-2 d-flex align-items-center gap-2 shadow-sm">
                                    <span className="fw-bold text-dark">{country.countryName}</span>
                                    <span className="text-muted">|</span>
                                    <div className="d-flex gap-1">
                                        {country.places?.map((p, i) => (
                                            <span key={i} className="badge bg-light text-dark border">{p.cityName} ({p.nights}N)</span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 3. OVERVIEW */}
                {data.description && (
                    <div className="mb-4 bg-white p-3 rounded border shadow-sm">
                        <h5 className="fw-bold text-dark border-bottom pb-2 mb-3">
                            <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-primary" /> Overview
                        </h5>
                        <p className="small text-muted mb-0" style={{ whiteSpace: 'pre-line' }}>{data.description}</p>
                    </div>
                )}

                {/* 4. DAILY ITINERARY */}
                {data.itinerary?.length > 0 && (
                    <div className="mb-4">
                        <h5 className="fw-bold text-dark border-bottom pb-2 mb-3">
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 text-primary" /> Itinerary
                        </h5>
                        <div className="position-relative ps-3" style={{ borderLeft: '2px solid #e9ecef', marginLeft: '10px' }}>
                            {data.itinerary.map((day, i) => (
                                <div key={i} className="mb-4 position-relative ps-4">
                                    <div style={{
                                        position: 'absolute', left: '-21px', top: '0',
                                        width: '40px', height: '40px', borderRadius: '50%',
                                        backgroundColor: '#fff', border: '3px solid #3b82f6',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontWeight: 'bold', fontSize: '12px', color: '#3b82f6'
                                    }}>
                                        D{day.day}
                                    </div>
                                    
                                    <div className="bg-white p-3 rounded border shadow-sm ms-2">
                                        <h6 className="fw-bold text-dark mb-1">{day.title || `Day ${day.day} Highlights`}</h6>
                                        {day.cityName && (
                                            <div className="text-primary small mb-2 fw-bold">
                                                <FontAwesomeIcon icon={faMoon} className="me-1" /> Overnight in {day.cityName}
                                            </div>
                                        )}
                                        <p className="text-muted small mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                                            {day.description || "No details added yet."}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 5. FLIGHTS (Completely Revamped) */}
                {data.flights?.included && data.flights?.options?.length > 0 && (
                    <div className="mb-4">
                        <h5 className="fw-bold text-dark border-bottom pb-2 mb-3">
                            <FontAwesomeIcon icon={faPlane} className="me-2 text-primary" /> Flight Details
                        </h5>
                        {data.flights.options.map((opt, idx) => (
                            <div key={idx} className="bg-white border rounded p-3 mb-2 shadow-sm">
                                {/* Option Header */}
                                <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                                    <div>
                                        <span className="fw-bold text-primary">{opt.airline}</span>
                                        <span className="badge bg-light text-dark border ms-2">{opt.cabin}</span>
                                    </div>
                                    {opt.pricePerPerson > 0 && (
                                        <div className="fw-bold text-success">
                                            {data.pricing?.currency} {opt.pricePerPerson} <small className="text-muted fw-normal">per person</small>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Detailed Segments */}
                                <div className="d-flex flex-column gap-3">
                                    {opt.segments?.map((seg, sIdx) => (
                                        <div key={sIdx} className="d-flex align-items-center justify-content-between small">
                                            {/* Departure */}
                                            <div className="text-center" style={{minWidth: '80px'}}>
                                                <div className="fw-bold fs-6">{formatTime(seg.departureTime)}</div>
                                                <div className="text-muted fw-bold">{seg.from}</div>
                                            </div>

                                            {/* Flight Path Graphic */}
                                            <div className="flex-grow-1 mx-3 text-center position-relative">
                                                <div style={{ borderTop: '2px dotted #ccc', position: 'absolute', top: '50%', width: '100%', zIndex: 0 }}></div>
                                                <div className="position-relative bg-white px-2 d-inline-block" style={{zIndex: 1}}>
                                                    <div className="fw-bold text-dark">{seg.airline}</div>
                                                    <div className="badge bg-secondary" style={{fontSize: '9px'}}>{seg.flightNo}</div>
                                                </div>
                                                <FontAwesomeIcon icon={faPlane} className="text-muted ms-1" />
                                            </div>

                                            {/* Arrival */}
                                            <div className="text-center" style={{minWidth: '80px'}}>
                                                <div className="fw-bold fs-6">{formatTime(seg.arrivalTime)}</div>
                                                <div className="text-muted fw-bold">{seg.to}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* 6. ACCOMMODATION */}
                {data.destinations?.some(d => d.places.some(p => p.hotels.length > 0)) && (
                    <div className="mb-4">
                        <h5 className="fw-bold text-dark border-bottom pb-2 mb-3">
                            <FontAwesomeIcon icon={faHotel} className="me-2 text-primary" /> Accommodation
                        </h5>
                        <div className="d-flex flex-column gap-3">
                            {data.destinations.map((dest) => (
                                dest.places.map((place, pIdx) => (
                                    place.hotels.length > 0 && (
                                        <div key={pIdx} className="d-flex align-items-start gap-3 bg-white p-3 rounded border shadow-sm">
                                            <div className="text-center p-2 bg-light rounded" style={{ minWidth: '80px' }}>
                                                <div className="fw-bold small">{place.cityName}</div>
                                                <div className="badge bg-dark text-white mt-1">{place.nights} Nights</div>
                                            </div>
                                            <div className="flex-grow-1">
                                                {place.hotels.map((hotel, hIdx) => (
                                                    <div key={hIdx} className="mb-2 last-mb-0">
                                                        {hotel.hotelOptions.map((opt, oIdx) => (
                                                            <div key={oIdx}>
                                                                <div className="fw-bold text-dark">
                                                                    {opt.name || "Hotel Name TBD"} 
                                                                    <span className="text-warning ms-2" style={{fontSize: '12px'}}>{"★".repeat(hotel.hotelCategory)}</span>
                                                                </div>
                                                                <div className="d-flex gap-3 small text-muted mt-1">
                                                                    <span><FontAwesomeIcon icon={faBed} /> {opt.roomType || "Std Room"}</span>
                                                                    <span><FontAwesomeIcon icon={faUtensils} /> {opt.mealPlan || "CP"}</span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )
                                ))
                            ))}
                        </div>
                    </div>
                )}

                {/* 7. INCLUSIONS & EXCLUSIONS */}
                <div className="row g-3">
                    <div className="col-12">
                        <div className="bg-white border rounded p-3 h-100 shadow-sm">
                            <h6 className="fw-bold text-success border-bottom pb-2 mb-2">
                                <FontAwesomeIcon icon={faCheckCircle} className="me-2" /> Inclusions
                            </h6>
                            <ul className="list-unstyled mb-0 small text-muted">
                                {data.inclusions?.length > 0 ? (
                                    data.inclusions.map((inc, i) => <li key={i} className="mb-1">✓ {inc}</li>)
                                ) : <li>No inclusions added.</li>}
                            </ul>
                        </div>
                    </div>
                    <div className="col-12">
                        <div className="bg-white border rounded p-3 h-100 shadow-sm">
                            <h6 className="fw-bold text-danger border-bottom pb-2 mb-2">
                                <FontAwesomeIcon icon={faTimesCircle} className="me-2" /> Exclusions
                            </h6>
                            <ul className="list-unstyled mb-0 small text-muted">
                                {data.exclusions?.length > 0 ? (
                                    data.exclusions.map((exc, i) => <li key={i} className="mb-1">✗ {exc}</li>)
                                ) : <li>No exclusions added.</li>}
                            </ul>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default DMCPackagePreview;